// frontend/src/hooks/useAudioCapture.js
// Unified audio capture hook supporting two modes:
//   'mic'    — getUserMedia (microphone / USB mic / VB-Cable)
//   'system' — getDisplayMedia (tab audio / YouTube / system sound)
//
// Returns a MediaStream with only audio tracks, resampled to 16 kHz PCM
// via a ScriptProcessor node when connected.

/**
 * Build a PCM sender from a MediaStream.
 * Resamples natively to 16 kHz regardless of source sample rate.
 * Returns { audioCtx, processor, stop }
 */
export function buildAudioPipeline(stream, onPcmChunk) {
  const nativeSr = stream.getAudioTracks()[0]?.getSettings()?.sampleRate || 48000;
  console.log(`[AudioCapture] native sample rate: ${nativeSr}`);

  const audioCtx = new (window.AudioContext || window.webkitAudioContext)({
    sampleRate: nativeSr,
  });

  const source    = audioCtx.createMediaStreamSource(stream);
  const processor = audioCtx.createScriptProcessor(4096, 1, 1);

  const ratio  = nativeSr / 16000;

  processor.onaudioprocess = (e) => {
    const floats = e.inputBuffer.getChannelData(0);
    const outLen = Math.round(floats.length / ratio);
    const pcm    = new Int16Array(outLen);
    for (let i = 0; i < outLen; i++) {
      const srcIdx = Math.min(Math.round(i * ratio), floats.length - 1);
      const s      = Math.max(-1, Math.min(1, floats[srcIdx]));
      pcm[i]       = s < 0 ? s * 0x8000 : s * 0x7fff;
    }
    onPcmChunk(pcm.buffer);
  };

  source.connect(processor);
  processor.connect(audioCtx.destination);

  const stop = () => {
    try { processor.disconnect(); } catch (_) {}
    try { audioCtx.close();       } catch (_) {}
    stream.getTracks().forEach(t => t.stop());
  };

  return { audioCtx, processor, stop };
}

/**
 * Capture microphone using getUserMedia.
 * audioConstraints = result of useAudioDevices().getAudioConstraints()
 */
export async function captureMic(audioConstraints) {
  const stream = await navigator.mediaDevices.getUserMedia(audioConstraints);
  return stream;
}

/**
 * Capture a browser tab / system audio using getDisplayMedia.
 * The user will see Chrome's "Share your screen" picker — they should:
 *   1. Click the "Chrome Tab" or "Window" tab
 *   2. Select the YouTube / interview tab
 *   3. Tick "Share tab audio" (bottom-left checkbox)
 *
 * Returns a stream containing only audio tracks (video track stopped immediately).
 */
export async function captureSystemAudio() {
  // Ask for both video+audio — Chrome requires video to be requested
  // for the "Share tab audio" checkbox to appear.
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { displaySurface: 'browser' },  // hint: prefer browser tab
    audio: {
      echoCancellation: false,  // don't process system audio
      noiseSuppression: false,
      autoGainControl:  false,
      sampleRate:       48000,  // most tab audio is 48 kHz
    },
  });

  // Stop video tracks immediately — we only need audio
  stream.getVideoTracks().forEach(t => t.stop());

  const audioTracks = stream.getAudioTracks();
  if (!audioTracks.length) {
    stream.getTracks().forEach(t => t.stop());
    throw new Error('No audio track captured. Did you tick "Share tab audio" in the picker?');
  }

  // Return an audio-only stream
  return new MediaStream(audioTracks);
}
